import { useSendNotification } from "@app/hooks/useNotification";
import { getMcpServerDisplayName } from "@app/lib/actions/mcp_helper";
import { getInternalMCPServerNameFromSId } from "@app/lib/actions/mcp_internal_actions/constants";
import type { MCPServerType } from "@app/lib/api/mcp";
import {
  composioToolkitForServer,
  isServerConnectedViaComposio,
} from "@app/lib/composio_toolkits";
import { clientFetch } from "@app/lib/egress/client";
import { useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type {
  GetComposioConnectionsResponseBody,
  PostComposioConnectResponseBody,
} from "@app/types/api/me/composio";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher } from "swr";

export function useComposioConnections({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const connectionsFetcher: Fetcher<GetComposioConnectionsResponseBody> =
    fetcher;

  const { data, error, mutate, isLoading } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/me/composio/connections`,
    connectionsFetcher
  );
  const toolkits = data?.toolkits;
  const isConnectedViaComposio = useCallback(
    (mcpServerId: string) =>
      isServerConnectedViaComposio(mcpServerId, toolkits ?? []),
    [toolkits]
  );

  return {
    isConnectedViaComposio,
    configured: data?.configured ?? false,
    isComposioConnectionsLoading: !disabled && isLoading,
    isComposioConnectionsError: error,
    mutateComposioConnections: mutate,
  };
}

export async function createComposioConnectRedirect({
  owner,
  serverName,
  callbackUrl,
}: {
  owner: LightWorkspaceType;
  serverName: string;
  callbackUrl?: string;
}): Promise<{ redirectUrl: string } | { error: string }> {
  const res = await clientFetch(`/api/w/${owner.sId}/me/composio/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serverName, callbackUrl }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message =
      body &&
      typeof body === "object" &&
      "error" in body &&
      body.error &&
      typeof body.error === "object" &&
      "message" in body.error &&
      typeof body.error.message === "string"
        ? body.error.message
        : `Failed to start Composio connect (${res.status})`;
    return { error: message };
  }

  const body: PostComposioConnectResponseBody = await res.json();
  return { redirectUrl: body.redirectUrl };
}

const composioConnectPollIntervalMs = 3_000;
const composioConnectTimeoutMs = 5 * 60_000;

export function useConnectViaComposio({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const sendNotification = useSendNotification();
  const { configured, mutateComposioConnections } = useComposioConnections({
    owner,
    disabled,
  });

  const canConnectViaComposio = useCallback(
    (mcpServerId: string) => {
      const serverName = getInternalMCPServerNameFromSId(mcpServerId);
      return (
        configured && !!serverName && !!composioToolkitForServer(serverName)
      );
    },
    [configured]
  );

  const connectViaComposio = useCallback(
    async (server: MCPServerType) => {
      const serverName = getInternalMCPServerNameFromSId(server.sId);
      if (!serverName) {
        return false;
      }
      const result = await createComposioConnectRedirect({
        owner,
        serverName,
        callbackUrl: window.location.href,
      });
      if ("error" in result) {
        sendNotification({
          type: "error",
          title: "Failed to connect",
          description: result.error,
        });
        return false;
      }
      window.open(result.redirectUrl, "_blank", "noopener,noreferrer");
      sendNotification({
        type: "info",
        title: `Connect ${getMcpServerDisplayName(server)}`,
        description:
          "Finish signing in in the new tab, then return here. Your connection will appear shortly.",
      });
      void mutateComposioConnections();
      return true;
    },
    [owner, sendNotification, mutateComposioConnections]
  );

  // Sign-in finishes in another tab, so the only completion signal is the
  // account turning up in the connections list.
  const waitForComposioConnection = useCallback(
    async (mcpServerId: string, isCancelled: () => boolean) => {
      const deadline = Date.now() + composioConnectTimeoutMs;
      while (Date.now() < deadline && !isCancelled()) {
        await new Promise((resolve) =>
          setTimeout(resolve, composioConnectPollIntervalMs)
        );
        const data = await mutateComposioConnections();
        if (isServerConnectedViaComposio(mcpServerId, data?.toolkits ?? [])) {
          return true;
        }
      }
      return false;
    },
    [mutateComposioConnections]
  );

  return {
    canConnectViaComposio,
    connectViaComposio,
    waitForComposioConnection,
  };
}
